import {
  formatGenerationDisplay,
  PICKER_REPORT_LIMITS,
  type AdminVehicleCatalogCreateGenerationBody,
  type AdminVehicleCatalogPatchGenerationBody,
  type AdminVehicleGenerationDto,
} from '@carcheck/shared';
import { z } from 'zod';

/**
 * The generation form (AUT-450 / VG-03). Bounds mirror the API's
 * `CreateGenerationSchema` (`vehicle-catalog.constants.ts`); Zod messages are
 * i18n keys (rule 11). Numbers are typed as text and parsed on submit.
 *
 * The display string (`formatGenerationDisplay`) must fit
 * `PICKER_REPORT_LIMITS.maxGenerationLength` — the preview goes red and Save
 * is disabled; the API's 422 `catalog_name_too_long` stays the backstop.
 */
export const GENERATION_LABEL_MAX = 40;
export const DISPLAY_MAX = PICKER_REPORT_LIMITS.maxGenerationLength;
const NUMBER = { min: 1, max: 99 };
const RESTYLING = { min: 0, max: 9 };
const YEAR = { min: 1900, max: 2100 };
const MARKET_MAX = 8;

const V = 'vehicleCatalog.generations.validation';

const inRange = (v: string, r: { min: number; max: number }): boolean =>
  /^\d+$/.test(v.trim()) && Number(v) >= r.min && Number(v) <= r.max;
const isYear = (v: string): boolean => inRange(v, YEAR);

export const generationFormSchema = z
  .object({
    generation: z.string().refine((v) => inRange(v, NUMBER), `${V}.generation`),
    restyling: z.number().int().min(RESTYLING.min).max(RESTYLING.max),
    market: z
      .string()
      .max(MARKET_MAX)
      .regex(/^[a-z]*$/),
    label: z
      .string()
      .trim()
      .min(1, `${V}.labelRequired`)
      .max(GENERATION_LABEL_MAX, `${V}.labelTooLong`),
    yearFrom: z.string().refine(isYear, `${V}.year`),
    yearTo: z.string(),
    inProduction: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.inProduction) return;
    if (!isYear(v.yearTo)) {
      ctx.addIssue({ code: 'custom', path: ['yearTo'], message: `${V}.year` });
    } else if (isYear(v.yearFrom) && Number(v.yearTo) < Number(v.yearFrom)) {
      ctx.addIssue({ code: 'custom', path: ['yearTo'], message: `${V}.yearOrder` });
    }
  });
export type GenerationFormValues = z.infer<typeof generationFormSchema>;

export const EMPTY_GENERATION: GenerationFormValues = {
  generation: '',
  restyling: 0,
  market: '',
  label: '',
  yearFrom: '',
  yearTo: '',
  inProduction: true,
};

export const generationValues = (g: AdminVehicleGenerationDto): GenerationFormValues => ({
  generation: String(g.generation),
  restyling: g.restyling,
  market: g.market,
  label: g.label,
  yearFrom: String(g.yearFrom),
  yearTo: g.yearTo === null ? '' : String(g.yearTo),
  inProduction: g.yearTo === null,
});

type Fields = Required<AdminVehicleCatalogCreateGenerationBody>;

const fieldsOf = (v: GenerationFormValues): Fields => ({
  generation: Number(v.generation),
  restyling: v.restyling,
  market: v.market,
  label: v.label.trim(),
  yearFrom: Number(v.yearFrom),
  yearTo: v.inProduction ? null : Number(v.yearTo),
});

export const generationCreateBody = (
  v: GenerationFormValues,
): AdminVehicleCatalogCreateGenerationBody => fieldsOf(v);

const FIELD_KEYS = ['generation', 'restyling', 'market', 'label', 'yearFrom', 'yearTo'] as const;

/** Only the changed keys + `updatedAt` verbatim; `null` = nothing changed. */
export const generationPatchBody = (
  v: GenerationFormValues,
  row: AdminVehicleGenerationDto,
): AdminVehicleCatalogPatchGenerationBody | null => {
  const next = fieldsOf(v);
  const changes: Partial<Fields> = {};
  for (const key of FIELD_KEYS) {
    if (next[key] !== row[key]) Object.assign(changes, { [key]: next[key] });
  }
  return Object.keys(changes).length === 0 ? null : { updatedAt: row.updatedAt, ...changes };
};

export interface GenerationPreview {
  text: string;
  length: number;
  over: boolean;
}

/** «В отчёте: …» — `null` until the label and the years are readable. */
export const generationPreview = (v: GenerationFormValues): GenerationPreview | null => {
  const label = v.label.trim();
  if (label === '' || !isYear(v.yearFrom)) return null;
  if (!v.inProduction && !isYear(v.yearTo)) return null;
  const text = formatGenerationDisplay({
    label,
    yearFrom: Number(v.yearFrom),
    yearTo: v.inProduction ? null : Number(v.yearTo),
  });
  return { text, length: text.length, over: text.length > DISPLAY_MAX };
};
